import { useEffect, useState } from "react";
import { Button, Form, Modal, Tab, Tabs, Table } from "react-bootstrap";
import useAlert from "../../components/CustomAlert";
import { useAuth } from "../../providers/authprovider";
import AddInstructionModal from "./AddInstructionModal"; // Adjust path as needed
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf } from "@fortawesome/free-regular-svg-icons";
import {
  faTrash,
  faPen,
  faPlusCircle,
} from "@fortawesome/free-solid-svg-icons";

export default function Instructions(props) {
  const { privilege } = props;
  const { alert, showAlert, hideAlert } = useAlert();
  const {
    instructions: {
      getInstructions,
      saveNewGroup,
      addInstruction,
      getInstructionFile,
      deleteInstruction,
      updateGroup,
    },
  } = useAuth();
  const [groups, setGroups] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [activeKey, setActiveKey] = useState("");
  const [showInstructionModal, setShowInstructionModal] = useState(false);
  const [instructionGroupId, setInstructionGroupId] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);

  useEffect(() => {
    const fetchInstructions = async () => {
      const result = await getInstructions();
      if (result.success) {
        setGroups(result.content);
        if (result.content.length > 0) {
          setActiveKey(result.content[0].group);
        }
      } else {
        showAlert("Failed to load instructions.", "danger");
      }
    };
    fetchInstructions();
  }, []);

  const handleCreateGroup = async () => {
    if (!newGroupName) return;

    hideAlert();

    try {
      const { success, content, error } = await saveNewGroup(newGroupName);
      if (!success) {
        showAlert("Failed to create group: " + error, "danger");
      } else {
        const updatedGroups = [...groups, content];
        setGroups(updatedGroups);
        setActiveKey(content.group);
      }
      setShowModal(false);
      setNewGroupName("");
    } catch (error) {
      showAlert("Failed to create group.", "danger");
    }
  };

  const handleUpdateGroup = async () => {
    if (!newGroupName || !editingGroup) return;

    hideAlert();

    try {
      const result = await updateGroup(editingGroup.id, newGroupName);
      if (result.success) {
        setGroups((prevGroups) =>
          prevGroups.map((g) =>
            g.id === editingGroup.id ? { ...g, group: newGroupName } : g
          )
        );
        setActiveKey(newGroupName);
        setShowModal(false);
        setNewGroupName("");
        setEditingGroup(null);
      } else {
        showAlert(
          "Nie udało się zaktualizować grupy: " + result.error,
          "danger"
        );
      }
    } catch (error) {
      showAlert("Błąd podczas aktualizacji grupy.", "danger");
    }
  };

  const handleSaveInstruction = async (formData, { onSuccess, onError }) => {
    const result = await addInstruction(instructionGroupId, formData);
    if (result.success) {
      setGroups((prevGroups) =>
        prevGroups.map((group) => {
          if (group.id === instructionGroupId) {
            return {
              ...group,
              instructions: [
                ...group.instructions,
                {
                  id: result.content.id,
                  title: result.content.title,
                },
              ],
            };
          }
          return group;
        })
      );
      onSuccess();
    } else {
      onError("Błąd dodawania instrukcji: " + result.error);
    }
  };

  const handlePreviewInstruction = async (instructionId) => {
    const result = await getInstructionFile(instructionId);
    if (!result.success) {
      showAlert("Nie udało się pobrać pliku.", "danger");
      return;
    }

    const blob = new Blob([result.file], {
      type: result.contentType || "application/pdf",
    });
    const fileURL = URL.createObjectURL(blob);
    window.open(fileURL, "_blank");
  };

  const handleDeleteInstruction = async (instructionId) => {
    if (!window.confirm("Czy na pewno chcesz usunąć tę instrukcję?")) return;

    const result = await deleteInstruction(instructionId);
    if (result.success) {
      setGroups((prevGroups) =>
        prevGroups.map((group) => {
          if (group.instructions.some((inst) => inst.id === instructionId)) {
            return {
              ...group,
              instructions: group.instructions.filter(
                (inst) => inst.id !== instructionId
              ),
            };
          }
          return group;
        })
      );
    } else {
      showAlert("Nie udało się usunąć instrukcji.", "danger");
    }
  };

  return (
    <div className="container p-2">
      {alert()}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Instrukcje</h4>
      </div>

      <Tabs
        activeKey={activeKey}
        onSelect={(k) => setActiveKey(k)}
        id="group-tabs"
        className=""
      >
        {groups.map((group, idx) => (
          <Tab
            eventKey={group.group}
            title={
              <>
                {group.group}{" "}
                {activeKey === group.group && privilege.edit && (
                  <FontAwesomeIcon
                    icon={faPen}
                    onClick={() => {
                      setEditingGroup(group);
                      setNewGroupName(group.group);
                      setShowModal(true);
                    }}
                    className="ms-2 text-muted"
                    style={{ cursor: "pointer" }}
                  />
                )}
              </>
            }
            key={idx}
          >
            <div
              className="tab-content-container border-start border-end border-bottom p-3"
              style={{ minHeight: "300px" }}
            >
              <div className="mb-2 text-end">
                {privilege.edit && (
                  <Button
                    variant="success"
                    onClick={() => {
                      setInstructionGroupId(group.id);
                      setShowInstructionModal(true);
                    }}
                  >
                    Dodaj instrukcję
                  </Button>
                )}
              </div>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th width={"100"}>L.p.</th>
                    <th>Nazwa</th>
                    <th width={"200"}>Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {group.instructions.map((inst, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{inst.title}</td>
                      <td>
                        <Button
                          variant="outline-success fs-5"
                          onClick={() => handlePreviewInstruction(inst.id)}
                        >
                          <FontAwesomeIcon icon={faFilePdf} />
                        </Button>
                        {privilege.edit && (
                          <Button
                            variant="outline-danger fs-5 ms-4"
                            onClick={() => handleDeleteInstruction(inst.id)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Tab>
        ))}
        {privilege.edit && (
          <Tab
            eventKey={"newGroup"}
            title={
              <>
                <FontAwesomeIcon
                  icon={faPlusCircle}
                  onClick={() => {
                    setEditingGroup(null);
                    setNewGroupName("");
                    setShowModal(true);
                  }}
                  className="ms-2 text-muted"
                  style={{ cursor: "pointer" }}
                />
              </>
            }
            key={"newGroup"}
          ></Tab>
        )}
      </Tabs>

      <AddInstructionModal
        show={showInstructionModal}
        onHide={() => setShowInstructionModal(false)}
        groupId={instructionGroupId}
        onSave={handleSaveInstruction}
      />

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingGroup ? "Edytuj grupę" : "Utwórz nową grupę"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              editingGroup ? handleUpdateGroup() : handleCreateGroup();
            }}
          >
            <Form.Group controlId="formGroupName">
              <Form.Label>Nazwa grupy</Form.Label>
              <Form.Control
                type="text"
                placeholder="wprowadź nazwę grupy"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Anuluj
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              editingGroup ? handleUpdateGroup() : handleCreateGroup();
            }}
          >
            Zapisz
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
